import { useLayoutEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Alert, Button, Icon, Input, Modal, StatusDot, useToast } from "@/shared/rd";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import {
  bindingPath,
  whatsappFailureKey,
  isUncertainWrite,
  useBindingTemplates,
  useWhatsappWabaWrite,
  useWhatsappWrite,
} from "../services/useWhatsapp";
import { compatibleTemplate } from "../services/template-components";
import {
  languageDisplayName,
  templateDisplayName,
} from "../services/value-format";
import type {
  WhatsappAccount,
  WhatsappBinding,
  WhatsappTemplate,
} from "../types/whatsapp.types";
import { QueryState, WriteFailure } from "./Shared";
import { SearchSelect } from "@/shared/rd";
import { WaField } from "./WaField";
import { muted, sectionHeading, summaryStyle } from "./styles";
const copy = keys.whatsapp;
function BindingRow({
  binding,
  templates,
}: {
  binding: WhatsappBinding;
  templates: WhatsappTemplate[];
}) {
  const t = useTranslations();
  const locale = useLocale();
  const [selected, setSelected] = useState("");
  const [action, setAction] = useState<"bind" | "disable">();
  const write = useWhatsappWrite();
  const toast = useToast();
  const [lifetime] = useState(capturePrivateSession);
  const busy = useRef(false);
  /** The last write that failed: its reason, and what to send again. */
  const [failure, setFailure] = useState<{ message: string; action: "bind" | "disable"; templateId: string }>();
  const [uncertain, setUncertain] = useState(false);
  const chosen = templates.find((item) => item.id === selected);
  const available = templates.filter((template) =>
    compatibleTemplate(template, binding),
  );
  const purpose = t(copy.purposes[binding.purpose]);
  async function submit(kind = action, templateId = selected) {
    if (busy.current || !kind || uncertain) return;
    busy.current = true;
    try {
      await write.mutateAsync({
        method: "PUT",
        path: bindingPath(binding.purpose),
        data:
          kind === "disable" ? { disabled: true } : { templateId },
      });
      if (isPrivateSessionCurrent(lifetime)) {
        setAction(undefined);
        setFailure(undefined);
        setSelected("");
        // The purpose names what changed; the sentence is the existing confirmation.
        toast.show({ message: `${purpose}: ${t(copy.bindingSaved)}` });
      }
    } catch (error) {
      if (isPrivateSessionCurrent(lifetime)) {
        const unknown = isUncertainWrite(error);
        setUncertain(Boolean(unknown));
        setFailure({ message: t(whatsappFailureKey(error, true)), action: kind, templateId });
        setAction(undefined);
      }
    } finally {
      busy.current = false;
    }
  }
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
        padding: "12px 0",
        borderBottom: "1px solid var(--rd-line-row)",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <h4 style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}>
          {t(copy.purposes[binding.purpose])}
        </h4>
        <StatusDot tone={binding.enabled ? "green" : "grey"}>
          {t(binding.enabled ? copy.enabled : copy.disabled)}
        </StatusDot>
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "var(--rd-text-secondary)", overflowWrap: "anywhere" }}>
        <bdi>{templateDisplayName(binding.templateName, t)}</bdi> ·{" "}
        <bdi>{languageDisplayName(binding.languageCode, locale)}</bdi>
      </p>
      <details style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
        <summary style={summaryStyle}>{t(copy.templateRequirements)}</summary>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
          <p style={{ margin: 0 }}>
            {t(copy.name)}: <bdi>{binding.templateName}</bdi>
          </p>
          <p style={{ margin: 0 }}>
            {t(copy.requiredParams, {
              parameters: binding.requiredBodyParameters.join(", "),
            })}
          </p>
          <p style={{ margin: 0 }}>
            {t(copy.requiredButtons, {
              count: binding.requiredQuickReplyButtons,
            })}
          </p>
          {binding.requiredQuickReplyLabels?.map((button) => (
            <p key={button.index} style={{ margin: 0 }}>
              {t(copy.requiredButtonLabels, {
                index: button.index + 1,
                labels: button.labels.join(" / "),
              })}
            </p>
          ))}
          {binding.requiredUrlButton && (
            <p style={{ margin: 0, overflowWrap: "anywhere" }}>
              {t(copy.requiredUrl, { url: binding.requiredUrlButton.url })}
            </p>
          )}
        </div>
      </details>
      <WaField label={t(copy.chooseTemplate)}>
        {(id) => (
          <SearchSelect
            id={id}
            label={t(copy.chooseTemplate)}
            placeholder={t(copy.chooseTemplate)}
            value={selected}
            onChange={(value) => {
              if (!write.isPending && !uncertain) setSelected(value);
            }}
            disabled={write.isPending || uncertain}
            options={[
              { value: "", label: t(copy.chooseTemplate) },
              ...available.map((template) => ({
                value: template.id,
                label: `${templateDisplayName(template.name, t)} · ${languageDisplayName(template.language, locale)}${
                  templateDisplayName(template.name, t) !== template.name
                    ? ` (${template.name})`
                    : ""
                }`,
              })),
            ]}
            emptyLabel={t(keys.common.noResults)}
          />
        )}
      </WaField>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <Button
          variant="primary"
          size="sm"
          disabled={!chosen || write.isPending || uncertain}
          onClick={() => setAction("bind")}
        >
          {t(copy.saveBinding)}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!binding.enabled || write.isPending || uncertain}
          onClick={() => setAction("disable")}
        >
          {t(copy.disable)}
        </Button>
      </div>
      {failure && (
        <WriteFailure
          title={t(failure.action === "disable" ? "whatsapp.bindingOffFailed" : "whatsapp.bindingApplyFailed", { purpose })}
          message={failure.message}
          uncertain={uncertain}
          retrying={write.isPending}
          onTryAgain={() => void submit(failure.action, failure.templateId)}
          onCheckedOutcome={() => {
            setUncertain(false);
            setFailure(undefined);
          }}
        />
      )}
      <Modal
        open={Boolean(action)}
        onClose={() => {
          if (!write.isPending) setAction(undefined);
        }}
        title={t(action === "disable" ? copy.disable : copy.saveBinding)}
        actions={
          <>
            <Button variant="ghost" muted disabled={write.isPending} onClick={() => setAction(undefined)}>
              {t(copy.cancel)}
            </Button>
            <Button
              variant={action === "disable" ? "danger" : "primary"}
              loading={write.isPending}
              loadingLabel={t(action === "disable" ? "whatsapp.turningOff" : "whatsapp.applying")}
              onClick={() => void submit()}
            >
              {t(copy.save)}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14 }}>
          {action === "disable"
            ? t(copy.disableConfirm, {
                purpose: t(copy.purposes[binding.purpose]),
              })
            : t(copy.confirmTemplate, {
                name: chosen?.name ?? "",
                language: chosen?.language ?? "",
              })}
        </p>
      </Modal>
    </div>
  );
}
function Bindings({ account }: { account: WhatsappAccount }) {
  const t = useTranslations();
  const locale = useLocale();
  const query = useBindingTemplates(account.readiness.managementReady);
  const templates = query.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t(copy.bindings)}</h3>
      <p style={muted}>{t(copy.bindingHint)}</p>
      <QueryState loading={query.isLoading} error={query.error} retry={query.refetch} page={t(copy.bindings).toLocaleLowerCase(locale)}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {account.bindings.map((binding) => (
            <BindingRow key={binding.purpose} binding={binding} templates={templates} />
          ))}
        </div>
        {query.hasNextPage && (
          <div style={{ paddingTop: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              loading={query.isFetchingNextPage}
              loadingLabel={t(keys.common.loading)}
              onClick={() => void query.fetchNextPage()}
            >
              {t(copy.moreTemplates)}
            </Button>
          </div>
        )}
      </QueryState>
    </section>
  );
}
export function AccountPanel({ account }: { account: WhatsappAccount }) {
  const t = useTranslations();
  const mutation = useWhatsappWabaWrite();
  const [value, setValue] = useState(account.businessAccountId ?? "");
  const setDirtySource = useNavigationGuard()?.setDirtySource;
  const dirty = value !== (account.businessAccountId ?? "");
  useLayoutEffect(() => {
    setDirtySource?.("content", dirty)
    return () => setDirtySource?.("content", false);
  }, [dirty, setDirtySource]);
  const toast = useToast();
  const [error, setError] = useState(false);
  const [lifetime] = useState(capturePrivateSession);
  async function saveAccountId() {
    if (mutation.isPending) return;
    try {
      await mutation.mutateAsync(value.trim());
      if (isPrivateSessionCurrent(lifetime)) {
        setError(false);
        toast.show({ message: t("whatsapp.toastAccountIdSaved") });
      }
    } catch {
      if (isPrivateSessionCurrent(lifetime)) setError(true);
    }
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0 }}>
      <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <h3 style={sectionHeading}>{t(copy.account)}</h3>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {(
            [
              "tokenConfigured",
              "phoneNumberIdConfigured",
              "businessAccountIdConfigured",
              "appSecretConfigured",
              "webhookVerifyTokenConfigured",
            ] as const
          ).map((field) => (
            <div
              key={field}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 0",
                borderBottom: "1px solid var(--rd-line-row)",
                fontSize: 14,
              }}
            >
              <span style={{ flex: 1 }}>{t(copy[field])}</span>
              <StatusDot tone={account.readiness[field] ? "green" : "red"}>
                {t(account.readiness[field] ? copy.ready : copy.missing)}
              </StatusDot>
            </div>
          ))}
        </div>
        <details open={!account.readiness.businessAccountIdConfigured}>
          <summary style={summaryStyle}>{t(copy.connectionSettings)}</summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
            <p style={muted}>
              {t(copy.sender)}: <bdi dir="ltr">{account.phoneNumberId ?? t(copy.notAvailable)}</bdi>
            </p>
            <form
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
              onSubmit={(event) => {
                event.preventDefault();
                void saveAccountId();
              }}
            >
              {error && (
                <WriteFailure
                  title={t("whatsapp.accountIdFailed")}
                  message={t(copy.writeFailed)}
                  uncertain={false}
                  retrying={mutation.isPending}
                  onTryAgain={() => void saveAccountId()}
                  onCheckedOutcome={() => setError(false)}
                />
              )}
              <WaField label={t(copy.waba)} help={t(copy.configHint)}>
                {(id) => (
                  <Input
                    id={id}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    pattern="[0-9]*"
                    maxLength={64}
                    dir="ltr"
                    disabled={mutation.isPending}
                  />
                )}
              </WaField>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={value === (account.businessAccountId ?? "")}
                  loading={mutation.isPending}
                  loadingLabel={t("whatsapp.saving")}
                >
                  {t(copy.configure)}
                </Button>
                <a
                  href={account.links.manager}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "var(--rd-primary)", textDecoration: "none" }}
                >
                  {t(copy.manager)}
                  <Icon icon={ExternalLink} size={14} />
                </a>
              </div>
            </form>
          </div>
        </details>
        {!account.readiness.managementReady && <Alert tone="warn" title={t("whatsapp.setupIncompleteTitle")}>{t(copy.configRequired)}</Alert>}
      </section>
      {account.readiness.managementReady && <Bindings account={account} />}
    </div>
  );
}
